import React, { useState, useEffect, useMemo, useCallback } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, ArrowLeft, Bed, Bath, Square, MapPin, Calendar, Home, Phone, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { Property } from "@shared/schema";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import PropertyModal from "@/components/PropertyModal";
// Removed CSS import because modal now styled inline

interface PropertyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  property: Property | null;
  onScheduleTour?: (property: Property) => void;
  onContactAgent?: (property: Property) => void;
  isLeadFormOpen?: boolean;
  leadType?: 'tour' | 'contact';
  onCloseLeadForm?: () => void;
  renderMode?: 'modal' | 'inline';
}

export default function PropertyDetailModal({ isOpen, onClose, property, onScheduleTour, onContactAgent, isLeadFormOpen, leadType, onCloseLeadForm, renderMode = 'modal' }: PropertyDetailModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [detailedProperty, setDetailedProperty] = useState<Property | null>(null);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  
  // Use detailed property if available, otherwise use basic property
  const displayProperty = detailedProperty || property;
  const images = useMemo(() => {
    if (!displayProperty) return [] as string[];
    const photoList = ((displayProperty as any)?.photos as string[]) ?? [];
    if (Array.isArray(photoList) && photoList.length > 0) {
      return photoList;
    }
    return displayProperty.images ?? [];
  }, [displayProperty]);

  const handleModalClose = useCallback(() => {
    if (isLeadFormOpen) return;
    onClose();
  }, [isLeadFormOpen, onClose]);
  
  // FIXED: Always call hooks FIRST before any conditional returns to prevent hooks order issues
  useImagePreloader(images, isOpen && !!property);
  
  // Reset state when property changes - MUST be before any early returns
  useEffect(() => {
    setCurrentImageIndex(0);
    setDetailedProperty(null);
    setIsLoadingImages(false);
  }, [property?.id]);

  // FORCE FRESH PHOTOS API CALL - Same logic as featured properties that work correctly
  useEffect(() => {
    if (isOpen && property) {
      // Start with basic property data immediately for instant UI
      setDetailedProperty(property);
      setIsLoadingImages(true);
      
      // ALWAYS fetch fresh enhanced data with ALL REAL IMAGES - force fresh API call
      console.log(`🚀 FORCING FRESH PHOTOS API for modal property ${property.id}`);
      fetch(`/api/properties/${property.id}/details`, { 
        cache: 'no-cache',  // Force fresh API call, no cached responses
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      })
        .then(response => response.json())
        .then(detailed => {
          console.log(`🖼️ Loaded ${detailed.images?.length || 0} images for property ${property.id}`);
          
          // Always update with detailed property data for enhanced image gallery
          if (detailed && detailed.images) {
            setDetailedProperty(detailed);
            console.log(`🔄 Enhanced: Upgraded from ${property.images?.length || 0} to ${detailed.images.length} images`);
          }
          setIsLoadingImages(false);
        })
        .catch(error => {
          console.error('Failed to load detailed property:', error);
          // Keep using basic property data
          setIsLoadingImages(false);
        });
    }
  }, [isOpen, property?.id]);  // Depend on property ID to ensure fresh fetch each time

  if (!property || !isOpen) return null;

  const formatPrice = (price: number | string | null | undefined): string => {
    if (!price) return 'Price on request';
    const numPrice = typeof price === 'string' ? parseInt(price) : price;
    return `$${numPrice.toLocaleString()}`;
  };

  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const detailContent = (
    <div className="flex flex-col max-w-full">
      <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white/95 sticky top-0 z-[1]">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Map
          </Button>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-gray-600 hover:text-gray-800"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="p-4 sm:p-6 space-y-8">
        {/* Inline Lead Form Overlay inside the dialog to avoid inert focus issues */}
        {isLeadFormOpen && property && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={(e) => {
            e.stopPropagation();
          }}>
            <div className="bg-white rounded-lg shadow-xl border max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <LeadFormOverlay isOpen={true} onClose={onCloseLeadForm || (() => {})} property={property} leadType={leadType || 'contact'} />
              </div>
            </div>
          </div>
        )}
        {/* Property Images - Large Gallery */}
        {images.length > 0 ? (
          <div className="relative mb-8 rounded-lg overflow-hidden">
            <div
              className="h-64 sm:h-80 md:h-96 lg:h-[500px] bg-cover bg-center bg-gray-200 transition-all duration-300"
              style={{
                backgroundImage: `url('${images[currentImageIndex]}')`
              }}
            >
              {/* Loading indicator when fetching detailed images */}
              {isLoadingImages && (
                <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Loading {detailedProperty ? 'high-res' : 'gallery'} photos...
                </div>
              )}
            </div>

            {/* Navigation arrows */}
            <Button
              variant="ghost"
              size="sm"
              onClick={prevImage}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-black/60 text-white hover:bg-black/80 h-10 w-10 rounded-full"
              disabled={images.length <= 1}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={nextImage}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-black/60 text-white hover:bg-black/80 h-10 w-10 rounded-full"
              disabled={images.length <= 1}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>

            {/* Image Counter */}
            <div className="absolute bottom-3 sm:bottom-4 right-3 sm:right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
              {currentImageIndex + 1} / {images.length}
              {isLoadingImages && images.length === 1 && " (Loading more...)"}
            </div>

            {/* Image Thumbnail Navigation */}
            {images.length > 1 && (
              <div className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1 sm:gap-2 max-w-xs overflow-x-auto">
                {images.slice(0, 8).map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-12 h-8 sm:w-16 sm:h-10 rounded overflow-hidden border-2 transition-all ${
                      currentImageIndex === index ? 'border-white' : 'border-transparent opacity-70'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Property ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </button>
                ))}
                {images.length > 8 && (
                  <div className="flex-shrink-0 w-12 h-8 sm:w-16 sm:h-10 rounded bg-black/60 flex items-center justify-center text-white text-xs">
                    +{images.length - 8}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="h-64 sm:h-80 md:h-96 lg:h-[500px] bg-gray-200 rounded-lg mb-8 flex items-center justify-center">
            <div className="text-gray-500 text-center">
              <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              Loading property images...
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Property Header */}
            <div className="mb-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-deep-navy mb-2">
                    {formatPrice(property.price)}
                  </h1>
                  <div className="flex items-start text-gray-600 mb-2">
                    <MapPin className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span className="text-sm sm:text-base">{property.address || `${property.city}, ${property.state}`}</span>
                  </div>
                </div>
                {property.propertyType && (
                  <Badge variant="secondary" className="text-sm">
                    {property.propertyType}
                  </Badge>
                )}
              </div>

              {/* Property Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    <Bed className="w-4 h-4 sm:w-5 sm:h-5 text-ocean-blue mr-1 sm:mr-2" />
                    <span className="text-xl sm:text-2xl font-bold text-ocean-blue">{property.bedrooms || 'N/A'}</span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Bedrooms</div>
                </div>
                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    <Bath className="w-4 h-4 sm:w-5 sm:h-5 text-ocean-blue mr-1 sm:mr-2" />
                    <span className="text-xl sm:text-2xl font-bold text-ocean-blue">{property.bathrooms || 'N/A'}</span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Bathrooms</div>
                </div>
                <div className="text-center p-3 sm:p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    <Square className="w-4 h-4 sm:w-5 sm:h-5 text-ocean-blue mr-1 sm:mr-2" />
                    <span className="text-lg sm:text-2xl font-bold text-ocean-blue">
                      {property.squareFeet ? property.squareFeet.toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Sq Ft</div>
                </div>
              </div>

              {/* Description */}
              {property.description && (
                <div className="mb-6">
                  <h3 className="font-playfair text-xl font-semibold text-deep-navy mb-3">Description</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {property.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 sticky top-6">
              <h3 className="font-playfair text-lg sm:text-xl font-semibold text-deep-navy mb-4">
                Interested in this property?
              </h3>
              
              <div className="space-y-3">
                <Button 
                  className="w-full bg-ocean-blue hover:bg-bright-blue text-white"
                  onClick={() => {
                    if (onScheduleTour && property) {
                      onScheduleTour(property);
                    }
                  }}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Schedule Tour
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full border-ocean-blue text-ocean-blue hover:bg-ocean-blue hover:text-white"
                  onClick={() => {
                    if (onContactAgent && property) {
                      onContactAgent(property);
                    }
                  }}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Contact Agent
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    if (onContactAgent && property) {
                      onContactAgent(property.id);
                    }
                    onClose();
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Request Info
                </Button>
              </div>

              {/* Property Details */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">Property Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Property Type</span>
                    <span className="font-medium">{property.propertyType || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Listing ID</span>
                    <span className="font-medium">{property.id}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (renderMode === 'inline') {
    return detailContent;
  }

  return (
    <PropertyModal
      open={isOpen}
      onClose={handleModalClose}
      variant="center"
      className="property-modal-root"
    >
      <VisuallyHidden>
        <h2 id="property-modal-title">Property Details</h2>
      </VisuallyHidden>
      {detailContent}
    </PropertyModal>
  );
}

// Lead Form Component with forced focus and original styling
export function LeadFormOverlay({ 
  isOpen, 
  onClose, 
  property, 
  leadType 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  property: Property | null; 
  leadType: 'tour' | 'contact'; 
}) {
  const [focusedField, setFocusedField] = useState<string | null>(null);

  if (!isOpen || !property) return null;

  const formatPrice = (price: number | undefined) => {
    if (!price) return 'Price not available';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (!numPrice) return 'Price on request';
    if (numPrice >= 1000000) {
      return `$${(numPrice / 1000000).toFixed(1)}M`;
    } else if (numPrice >= 1000) {
      return `$${(numPrice / 1000).toFixed(0)}K`;
    }
    return `$${numPrice.toLocaleString()}`;
  };

  const handleInputClick = (fieldName: string, inputRef: React.RefObject<HTMLInputElement>) => {
    setFocusedField(fieldName);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.click();
      }
    }, 10);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const data = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      preferredTime: leadType === 'tour' ? formData.get('preferredTime') : undefined,
      message: '',
      propertyId: property.id,
      leadType: leadType
    };
    
    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        alert('Thank you! We\'ll be in touch soon.');
        onClose();
      } else {
        alert('Error submitting request. Please try again.');
      }
    } catch (error) {
      alert('Error submitting request. Please try again.');
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50"
      style={{ 
        zIndex: 999999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'auto'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl border max-w-md w-full mx-4"
        style={{ 
          zIndex: 1000000, 
          position: 'relative',
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-luxury-gold rounded-full flex items-center justify-center mx-auto mb-3">
              <Calendar className="w-6 h-6 text-deep-navy" />
            </div>
            <h2 className="font-playfair text-xl text-deep-navy mb-2">
              {leadType === 'tour' ? 'Schedule Tour' : 'Contact Agent'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {leadType === 'tour' ? 'Schedule a property tour' : 'Get in touch with our agent'}
            </p>
            
            {/* Property Information */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-center">
              <h4 className="font-semibold text-lg text-deep-navy mb-2">
                {formatPrice(property.price)}
              </h4>
              <p className="text-sm text-gray-600 mb-1">
                {property.bedrooms || 'N/A'} bed, {property.bathrooms || 'N/A'} bath
              </p>
              <p className="text-xs text-gray-500">
                {property.address || `${property.city}, ${property.state}`}
              </p>
            </div>
          </div>
          
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input 
                  type="text" 
                  name="firstName"
                  placeholder="First Name" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ 
                    fontSize: '16px',
                    backgroundColor: 'white',
                    color: 'black'
                  }}
                  required 
                  autoFocus
                  onFocus={() => setFocusedField('firstName')}
                  onBlur={() => setFocusedField(null)}
                />
              </div>
              <div>
                <input 
                  type="text" 
                  name="lastName"
                  placeholder="Last Name" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ 
                    fontSize: '16px',
                    backgroundColor: 'white',
                    color: 'black'
                  }}
                  required 
                  onFocus={() => setFocusedField('lastName')}
                  onBlur={() => setFocusedField(null)}
                />
              </div>
            </div>
            
            <div>
              <input 
                type="email" 
                name="email"
                placeholder="Email Address" 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ 
                  fontSize: '16px',
                  backgroundColor: 'white',
                  color: 'black'
                }}
                required 
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
            
            <div>
              <input 
                type="tel" 
                name="phone"
                placeholder="Phone Number" 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ 
                  fontSize: '16px',
                  backgroundColor: 'white',
                  color: 'black'
                }}
                required 
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
            
            {leadType === 'tour' && (
              <div>
                <select 
                  name="preferredTime"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ 
                    fontSize: '16px',
                    backgroundColor: 'white',
                    color: 'black'
                  }}
                >
                  <option value="">Preferred Time</option>
                  <option value="morning">Morning (9-12 PM)</option>
                  <option value="afternoon">Afternoon (12-5 PM)</option>
                  <option value="evening">Evening (5-7 PM)</option>
                </select>
              </div>
            )}
            
            <div className="flex gap-4 pt-4">
              <button 
                type="button" 
                onClick={onClose}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                style={{ fontSize: '16px' }}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                style={{ fontSize: '16px' }}
              >
                {leadType === 'tour' ? 'Schedule Tour' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}